import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  DollarSign, 
  Target,
  Zap,
  ChevronRight,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Plus,
  Timer,
  Flame,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Brain,
  Lightbulb,
  Star,
  Trophy,
  Bookmark,
  Bell,
  Settings,
  Filter,
  TrendingUp as TrendIcon,
  MoreHorizontal,
  CreditCard,
  PiggyBank,
  Gauge,
  Clock,
  ChartBar,
  AlertCircle,
  CheckCircle2,
  Info,
  CircleDot
} from 'lucide-react';

import { useBudget } from '../../contexts/BudgetContext';
import { getDashboardTotalBudget } from '../../utils/budgetCalculations';
import { realTransactions } from '../../utils/transactionFilters';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import InsightsSummaryCard from '../Insights/InsightsSummaryCard';
import { getAllInsights } from '../../services/insightsAPI'; 
import { useCurrency } from '../../contexts/CurrencyContext';
import QuickActions from './QuickActions';
import InlineInsightCards from './InlineInsightCards';
import MonthRaceTrack from './MonthRaceTrack';
import DaysOfRunway from './DaysOfRunway';
import PaycheckProgressCard from '../Paycheck/PaycheckProgressCard';
import DashboardLedgers from './DashboardLedgers';
import { useAccount } from '../../contexts/AccountContext';

// Mobile-first responsive styles
const createStyles = (themeMode, isMobile, isTablet) => ({
  container: {
    minHeight: '100vh',
    background: themeMode === 'light' 
      ? 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #f0fdf4 100%)'
      : 'transparent',
    padding: isMobile ? '8px' : isTablet ? '12px' : '16px',
    overflow: 'hidden',
    position: 'relative',
    transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1), color 0.4s ease'
  },
  backgroundElements: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0
  },
  bgCircle1: {
    position: 'absolute',
    top: '15%',
    left: '10%',
    width: isMobile ? '200px' : isTablet ? '300px' : '384px',
    height: isMobile ? '200px' : isTablet ? '300px' : '384px',
    background: themeMode === 'light' 
      ? 'rgba(59, 130, 246, 0.05)'
      : 'rgba(59, 130, 246, 0.1)',
    borderRadius: '50%',
    filter: 'blur(48px)',
    animation: 'pulse 2s infinite'
  },
  bgCircle2: {
    position: 'absolute',
    bottom: '15%',
    right: '10%',
    width: isMobile ? '200px' : isTablet ? '300px' : '384px',
    height: isMobile ? '200px' : isTablet ? '300px' : '384px',
    background: themeMode === 'light' 
      ? 'rgba(147, 51, 234, 0.05)'
      : 'rgba(147, 51, 234, 0.1)',
    borderRadius: '50%',
    filter: 'blur(48px)',
    animation: 'pulse 2s infinite',
    animationDelay: '1s'
  },
  bgCircle3: {
    position: 'absolute',
    top: '60%',
    left: '50%',
    width: isMobile ? '150px' : isTablet ? '200px' : '256px',
    height: isMobile ? '150px' : isTablet ? '200px' : '256px',
    background: themeMode === 'light' 
      ? 'rgba(236, 72, 153, 0.05)'
      : 'rgba(236, 72, 153, 0.1)',
    borderRadius: '50%',
    filter: 'blur(48px)',
    animation: 'pulse 2s infinite',
    animationDelay: '2s',
    transform: 'translateX(-50%)'
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 10,
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%'
  },
  glassCard: {
    position: 'relative',
    background: themeMode === 'light' 
      ? 'rgba(255, 255, 255, 0.85)'
      : 'rgba(255, 255, 255, 0.06)',
    backdropFilter: 'blur(20px) saturate(180%)',
    border: themeMode === 'light' 
      ? '1px solid rgba(255, 255, 255, 0.9)'
      : '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: isMobile ? '12px' : '16px',
    padding: isMobile ? '16px' : isTablet ? '20px' : '24px',
    height: '100%',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    boxShadow: themeMode === 'light'
      ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.04)'
      : '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)'
  },
  glassCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: themeMode === 'light' 
      ? '0 4px 6px rgba(0,0,0,0.07), 0 12px 32px rgba(99,102,241,0.12), 0 20px 48px rgba(0,0,0,0.06)'
      : '0 4px 6px rgba(0,0,0,0.5), 0 12px 32px rgba(99,102,241,0.2)'
  },
  headerSection: {
    marginBottom: isMobile ? '16px' : isTablet ? '24px' : '32px'
  },
  headerCard: {
    marginBottom: isMobile ? '12px' : isTablet ? '18px' : '24px'
  },
  headerTitle: {
    fontSize: isMobile ? '20px' : isTablet ? '24px' : '30px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    marginBottom: isMobile ? '4px' : '8px',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    flexWrap: isMobile ? 'wrap' : 'nowrap'
  },
  headerSubtitle: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '4px' : '8px',
    fontSize: isMobile ? '12px' : '14px',
    flexWrap: 'wrap'
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    marginTop: isMobile ? '12px' : '0'
  },
  headerWrapper: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    gap: isMobile ? '12px' : '0'
  },
  privacyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    padding: isMobile ? '8px 12px' : '8px 16px',
    background: themeMode === 'light' 
      ? 'rgba(99, 102, 241, 0.1)'
      : 'rgba(255, 255, 255, 0.1)',
    border: themeMode === 'light' 
      ? '1px solid rgba(99, 102, 241, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: isMobile ? '10px' : '12px',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: '44px', // Touch target
    fontSize: isMobile ? '12px' : '14px'
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    padding: isMobile ? '6px 10px' : '8px 12px',
    background: 'rgba(16, 185, 129, 0.2)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    borderRadius: isMobile ? '10px' : '12px',
    minHeight: '44px'
  },
  liveText: {
    color: '#10b981',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile 
      ? '1fr' 
      : isTablet 
        ? 'repeat(2, 1fr)' 
        : 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: isMobile ? '12px' : isTablet ? '16px' : '24px',
    marginBottom: isMobile ? '16px' : isTablet ? '24px' : '32px'
  },
  cardHeight: {
    height: isMobile ? '200px' : isTablet ? '220px' : '256px'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: isMobile ? '12px' : '16px',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    gap: isMobile ? '8px' : '0'
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    flex: 1,
    minWidth: 0
  },
  iconContainer: {
    padding: isMobile ? '8px' : '12px',
    borderRadius: isMobile ? '10px' : '12px',
    border: '1px solid',
    flexShrink: 0
  },
  iconContainerGreen: {
    background: 'rgba(16, 185, 129, 0.12)',
    borderColor: 'rgba(16, 185, 129, 0.3)',
    borderRadius: '50%',
    padding: '6px'
  },
  iconContainerRed: {
    background: 'rgba(244, 63, 94, 0.12)',
    borderColor: 'rgba(244, 63, 94, 0.3)',
    borderRadius: '50%',
    padding: '6px'
  },
  iconContainerBlue: {
    background: 'rgba(99, 102, 241, 0.12)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
    borderRadius: '50%',
    padding: '6px'
  },
  cardTitle: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  },
  cardSubtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    marginTop: '4px',
    flexWrap: 'wrap'
  },
  trendBadge: {
    fontSize: isMobile ? '10px' : '12px',
    padding: isMobile ? '2px 6px' : '2px 8px',
    borderRadius: isMobile ? '8px' : '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },
  trendBadgeGreen: {
    color: '#10b981',
    background: 'rgba(16, 185, 129, 0.2)'
  },
  trendBadgeRed: {
    color: '#f43f5e',
    background: 'rgba(244, 63, 94, 0.2)'
  },
  secondaryText: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: isMobile ? '10px' : '12px'
  },
  mainAmount: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minHeight: isMobile ? '60px' : '80px'
  },
  amountText: {
    fontSize: isMobile ? '24px' : isTablet ? '30px' : '36px',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    lineHeight: 1.2
  },
  amountTextGreen: {
    color: '#10b981'
  },
  amountTextRed: {
    color: '#f43f5e'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: isMobile ? '8px' : '16px',
    marginTop: isMobile ? '12px' : '16px'
  },
  statItem: {
    textAlign: 'center'
  },
  statLabel: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '2px'
  },
  statValue: {
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    fontWeight: '700',
    fontSize: '16px'
  },
  budgetGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '2fr 1fr',
    gap: isMobile ? '16px' : isTablet ? '20px' : '24px',
    marginBottom: isMobile ? '16px' : isTablet ? '24px' : '32px'
  },
  budgetCard: {
    height: 'auto',
    minHeight: isMobile ? '280px' : '320px',
    overflow: 'visible'
  },
  budgetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: isMobile ? '16px' : '24px',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    gap: isMobile ? '12px' : '0'
  },
  budgetTitle: {
    fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc'
  },
  budgetSubtitle: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: isMobile ? '12px' : '14px'
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    padding: isMobile ? '6px 10px' : '8px 12px',
    borderRadius: isMobile ? '10px' : '12px',
    border: '1px solid',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600',
    minHeight: '36px'
  },
  statusBadgeGreen: {
    background: 'rgba(16, 185, 129, 0.2)',
    borderColor: 'rgba(16, 185, 129, 0.3)',
    color: '#10b981'
  },
  statusBadgeRed: {
    background: 'rgba(244, 63, 94, 0.2)',
    borderColor: 'rgba(244, 63, 94, 0.3)',
    color: '#f43f5e'
  },
  statusBadgeYellow: {
    background: 'rgba(245, 158, 11, 0.2)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
    color: '#f59e0b'
  },
  budgetStats: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
    gap: isMobile ? '12px' : '24px',
    marginBottom: isMobile ? '16px' : '24px'
  },
  budgetStatItem: {
    textAlign: 'center',
    padding: isMobile ? '8px' : '0'
  },
  budgetStatLabel: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '4px'
  },
  budgetStatValue: {
    fontSize: isMobile ? '18px' : isTablet ? '20px' : '24px',
    fontWeight: '700',
    letterSpacing: '-0.02em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc'
  },
  progressContainer: {
    position: 'relative',
    marginBottom: isMobile ? '12px' : '16px'
  },
  progressBar: {
    height: '6px',
    background: themeMode === 'light' 
      ? 'rgba(0, 0, 0, 0.06)'
      : 'rgba(255, 255, 255, 0.06)',
    borderRadius: 9999,
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: 9999,
    transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
  },
  progressFillNormal: {
    background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)'
  },
  progressFillOver: {
    background: 'linear-gradient(90deg, #f43f5e 0%, #fb7185 100%)'
  },
  progressIndicator: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    background: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)'
  },
  progressLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: isMobile ? '12px' : '14px',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)'
  },
  progressCenter: {
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    fontWeight: '600'
  },
  insightCard: {
    marginTop: isMobile ? '12px' : '16px',
    padding: isMobile ? '12px' : '16px',
    background: themeMode === 'light' 
      ? 'rgba(99, 102, 241, 0.05)'
      : 'rgba(255, 255, 255, 0.05)',
    border: themeMode === 'light' 
      ? '1px solid rgba(99, 102, 241, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: isMobile ? '10px' : '12px'
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '6px' : '8px',
    marginBottom: isMobile ? '6px' : '8px'
  },
  insightTitle: {
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600'
  },
  insightText: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: isMobile ? '12px' : '14px',
    lineHeight: 1.4
  },
  sidebarStats: {
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    gap: isMobile ? '12px' : '24px',
    overflow: isMobile ? 'auto' : 'visible'
  },
  sidebarStatCard: {
    minWidth: isMobile ? '200px' : 'auto',
    flex: isMobile ? '0 0 auto' : '1'
  },
  progressRingContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: isMobile ? '12px' : '16px'
  },
  progressRingStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: isMobile ? '12px' : '16px',
    textAlign: 'center'
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isMobile ? '44px' : '40px',
    height: isMobile ? '44px' : '40px',
    background: themeMode === 'light' 
      ? 'rgba(99, 102, 241, 0.1)'
      : 'rgba(255, 255, 255, 0.1)',
    border: themeMode === 'light' 
      ? '1px solid rgba(99, 102, 241, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: isMobile ? '10px' : '12px',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: isMobile ? '4px 8px' : '4px 8px',
    fontSize: isMobile ? '10px' : '12px',
    fontWeight: '600',
    borderRadius: isMobile ? '6px' : '8px',
    border: '1px solid',
    whiteSpace: 'nowrap'
  },
  chipOutlined: {
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    background: 'transparent',
    borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(248, 250, 252, 0.2)'
  },
  // New Budget Control Center styles
  budgetVisualStatCard: {
    padding: isMobile ? '12px' : '16px',
    borderRadius: isMobile ? '8px' : '12px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '10px' : '12px',
    flex: 1,
    minWidth: 0
  },
  budgetStatCardBlue: {
    background: 'rgba(99, 102, 241, 0.1)',
    borderColor: 'rgba(99, 102, 241, 0.3)'
  },
  budgetStatCardGreen: {
    background: 'rgba(16, 185, 129, 0.1)',
    borderColor: 'rgba(16, 185, 129, 0.3)'
  },
  budgetStatCardAmber: {
    background: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.3)'
  },
  budgetStatCardRed: {
    background: 'rgba(244, 63, 94, 0.1)',
    borderColor: 'rgba(244, 63, 94, 0.3)'
  },
  budgetStatCardIcon: {
    padding: isMobile ? '8px' : '10px',
    borderRadius: isMobile ? '6px' : '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    flexShrink: 0
  },
  budgetStatCardContent: {
    flex: 1,
    minWidth: 0
  },
  budgetStatCardLabel: {
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '4px'
  },
  budgetStatCardValue: {
    fontSize: isMobile ? '16px' : '20px',
    fontWeight: '700',
    letterSpacing: '-0.02em',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc'
  },
  enhancedProgressBar: {
    position: 'relative',
    marginBottom: isMobile ? '16px' : '20px'
  },
  progressTrack: {
    height: '6px',
    background: themeMode === 'light' 
      ? 'rgba(0, 0, 0, 0.06)'
      : 'rgba(255, 255, 255, 0.06)',
    borderRadius: 9999,
    overflow: 'hidden',
    position: 'relative'
  },
  progressFillGradient: {
    height: '100%',
    borderRadius: 9999,
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
    transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
    position: 'relative',
    overflow: 'hidden'
  },
  progressTickMarks: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 25%'
  },
  progressTick: {
    width: '2px',
    height: '100%',
    background: themeMode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(248, 250, 252, 0.2)'
  },
  timeProgressMarker: {
    position: 'absolute',
    top: '-4px',
    bottom: '-4px',
    width: '2px',
    background: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    zIndex: 2,
    boxShadow: '0 0 8px rgba(0,0,0,0.3)'
  },
  paceDot: {
    position: 'absolute',
    top: '-8px',
    transform: 'translateX(-50%)',
    width: isMobile ? '12px' : '14px',
    height: isMobile ? '12px' : '14px',
    borderRadius: '50%',
    border: '2px solid ' + (themeMode === 'light' ? '#ffffff' : '#44444e'),
    zIndex: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    animation: 'pulse 2s infinite'
  },
  progressLabelsEnhanced: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: isMobile ? '11px' : '12px',
    color: themeMode === 'light' ? '#64748b' : 'rgba(248, 250, 252, 0.6)',
    marginTop: '8px'
  },
  categoryBudgetSection: {
    marginTop: isMobile ? '16px' : '20px',
    paddingTop: isMobile ? '16px' : '20px',
    borderTop: `1px solid ${themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(248, 250, 252, 0.1)'}`
  },
  categoryBudgetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: isMobile ? '12px' : '16px'
  },
  categoryBudgetTitle: {
    fontSize: isMobile ? '13px' : '14px',
    fontWeight: '600',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc'
  },
  categoryBudgetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '8px' : '12px',
    marginBottom: isMobile ? '10px' : '12px',
    padding: isMobile ? '8px' : '10px',
    borderRadius: isMobile ? '6px' : '8px',
    background: themeMode === 'light' 
      ? 'rgba(0, 0, 0, 0.03)'
      : 'rgba(248, 250, 252, 0.03)',
    border: '1px solid',
    borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(248, 250, 252, 0.05)',
    borderLeft: '3px solid',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  categoryBudgetIcon: {
    flexShrink: 0,
    fontSize: isMobile ? '16px' : '18px'
  },
  categoryBudgetInfo: {
    flex: 1,
    minWidth: 0
  },
  categoryBudgetName: {
    fontSize: isMobile ? '12px' : '13px',
    fontWeight: '500',
    color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
    marginBottom: '4px'
  },
  categoryBudgetProgressBar: {
    height: '6px',
    background: themeMode === 'light' 
      ? 'rgba(0, 0, 0, 0.06)'
      : 'rgba(255, 255, 255, 0.06)',
    borderRadius: 9999,
    overflow: 'hidden',
    position: 'relative'
  },
  categoryBudgetProgressFill: {
    height: '100%',
    borderRadius: 9999,
    transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
  },
  categoryBudgetAmount: {
    fontSize: isMobile ? '11px' : '12px',
    fontWeight: '600',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  showMoreButton: {
    width: '100%',
    padding: isMobile ? '8px' : '10px',
    background: 'transparent',
    border: '1px dashed',
    borderColor: themeMode === 'light' ? 'rgba(31, 41, 55, 0.2)' : 'rgba(255, 255, 255, 0.2)',
    borderRadius: isMobile ? '6px' : '8px',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    fontSize: isMobile ? '12px' : '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  },
  safeToSpendCard: {
    marginTop: isMobile ? '16px' : '20px',
    padding: isMobile ? '14px' : '16px',
    borderRadius: isMobile ? '10px' : '12px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '12px' : '14px'
  },
  safeToSpendGreen: {
    background: 'rgba(34, 197, 94, 0.1)',
    borderColor: 'rgba(34, 197, 94, 0.3)'
  },
  safeToSpendAmber: {
    background: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.3)'
  },
  safeToSpendRed: {
    background: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.3)'
  },
  safeToSpendIcon: {
    padding: isMobile ? '10px' : '12px',
    borderRadius: isMobile ? '8px' : '10px',
    background: 'rgba(255, 255, 255, 0.15)',
    flexShrink: 0
  },
  safeToSpendContent: {
    flex: 1
  },
  safeToSpendAmount: {
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: '800',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    lineHeight: 1.2,
    marginBottom: '4px'
  },
  safeToSpendLabel: {
    fontSize: isMobile ? '12px' : '13px',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    fontWeight: '500'
  },
  speedMetricsSparkline: {
    marginTop: isMobile ? '12px' : '16px',
    marginBottom: isMobile ? '12px' : '16px',
    padding: isMobile ? '12px' : '14px',
    background: themeMode === 'light' 
      ? 'rgba(31, 41, 55, 0.03)'
      : 'rgba(255, 255, 255, 0.03)',
    borderRadius: isMobile ? '8px' : '10px',
    border: '1px solid',
    borderColor: themeMode === 'light' ? 'rgba(31, 41, 55, 0.05)' : 'rgba(255, 255, 255, 0.05)'
  },
  speedMetricsSparklineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: isMobile ? '8px' : '10px'
  },
  speedMetricsSparklineTitle: {
    fontSize: isMobile ? '11px' : '12px',
    fontWeight: '600',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  speedMetricsComparison: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: isMobile ? '10px' : '11px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  comparisonUp: {
    color: '#ef4444',
    background: 'rgba(239, 68, 68, 0.1)'
  },
  comparisonDown: {
    color: '#22c55e',
    background: 'rgba(34, 197, 94, 0.1)'
  }
});

// Enhanced floating card with mobile optimization and improved interactions
const FloatingCard = ({ 
  children, 
  style = {}, 
  hover3D = true, 
  glowColor = 'rgba(147, 197, 253, 0.3)',
  delay = 0,
  onClick,
  themeMode,
  isMobile,
  interactive = true,
  className = '',
  ...props 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  const styles = createStyles(themeMode, isMobile);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || !hover3D || isMobile) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    
    setMousePosition({ x, y });
  }, [hover3D, isMobile]);

  const handleTouch = useCallback(() => {
    if (isMobile && interactive) {
      setIsPressed(true);
      setTimeout(() => setIsPressed(false), 150);
    }
  }, [isMobile, interactive]);

  const cardStyle = {
    ...styles.glassCard,
    ...style,
    ...(isHovered && interactive && styles.glassCardHover),
    transform: hover3D && isHovered && !isMobile && interactive
      ? `perspective(1000px) rotateY(${mousePosition.x * 0.3}deg) rotateX(${-mousePosition.y * 0.3}deg) translateZ(10px)`
      : isPressed && isMobile
        ? 'scale(0.98) translateY(2px)'
        : 'perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)',
    animationDelay: `${delay}ms`,
    willChange: 'transform',
    ...(interactive && {
      cursor: onClick ? 'pointer' : 'default'
    })
  };

  return (
    <div
      ref={cardRef}
      style={cardStyle}
      className={className}
      onMouseEnter={() => !isMobile && interactive && setIsHovered(true)}
      onMouseLeave={() => !isMobile && interactive && setIsHovered(false)}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouch}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      <div style={{
        position: 'absolute',
        top: '-2px',
        left: '-2px',
        right: '-2px',
        bottom: '-2px',
        borderRadius: isMobile ? '12px' : '16px',
        background: `radial-gradient(circle at 50% 50%, ${glowColor}, transparent 70%)`,
        opacity: (isHovered || isPressed) && interactive ? 0.6 : 0,
        transition: 'opacity 0.5s ease',
        zIndex: -1
      }} />
      
      {children}
    </div>
  );
};

// Responsive animated counter
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 2000, isMobile }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(value * easeOutQuart));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  
  return (
    <span style={{ fontWeight: '700', fontSize: 'inherit' }}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

// Mobile-optimized progress ring
const ProgressRing = ({ 
  progress, 
  size = 80, 
  strokeWidth = 6, 
  color = '#3B82F6',
  backgroundColor = 'rgba(255,255,255,0.1)',
  children,
  animated = true,
  isMobile
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const actualSize = isMobile ? Math.min(size, 80) : size;
  const actualStrokeWidth = isMobile ? Math.max(strokeWidth - 2, 4) : strokeWidth;
  
  useEffect(() => {
    if (animated) {
      const timer = setTimeout(() => setAnimatedProgress(progress), 200);
      return () => clearTimeout(timer);
    } else {
      setAnimatedProgress(progress);
    }
  }, [progress, animated]);
  
  const radius = (actualSize - actualStrokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (animatedProgress / 100) * circumference;
  
  return (
    <div style={{ position: 'relative', width: actualSize, height: actualSize }}>
      <svg width={actualSize} height={actualSize} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={actualStrokeWidth}
          fill="transparent"
        />
        <circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke={color}
          strokeWidth={actualStrokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 1s ease-out',
          }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {children}
      </div>
    </div>
  );
};

// Mobile-optimized sparkline
const Sparkline = ({ data, color = '#3B82F6', width = 60, height = 30, isMobile }) => {
  if (!data || data.length === 0) return null;
  
  const actualWidth = isMobile ? Math.min(width, 60) : width;
  const actualHeight = isMobile ? Math.min(height, 30) : height;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = data.length > 1 ? (index / (data.length - 1)) * actualWidth : actualWidth / 2;
    const y = actualHeight - ((value - min) / range) * actualHeight;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={actualWidth} height={actualHeight} style={{ overflow: 'visible', flexShrink: 0 }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={isMobile ? "1.5" : "2"}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
      />
    </svg>
  );
};

// Enhanced Loading Component with skeleton cards
const SkeletonCard = ({ height = 256, themeMode, isMobile }) => (
  <div style={{
    height,
    background: themeMode === 'light' 
      ? 'rgba(255, 255, 255, 0.8)'
      : 'rgba(255, 255, 255, 0.05)',
    borderRadius: isMobile ? '12px' : '16px',
    padding: isMobile ? '16px' : '24px',
    border: themeMode === 'light' 
      ? '1px solid rgba(255, 255, 255, 0.3)'
      : '1px solid rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '12px' : '16px'
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{
        width: '60%',
        height: isMobile ? '16px' : '20px',
        background: themeMode === 'light' 
          ? 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)'
          : 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
        borderRadius: '4px',
        animation: 'shimmer 2s infinite linear'
      }} />
      <div style={{
        width: isMobile ? '40px' : '60px',
        height: isMobile ? '20px' : '30px',
        background: themeMode === 'light' 
          ? 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)'
          : 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
        borderRadius: '4px',
        animation: 'shimmer 2s infinite linear',
        animationDelay: '0.5s'
      }} />
    </div>
    
    <div style={{
      width: '40%',
      height: isMobile ? '32px' : '48px',
      background: themeMode === 'light' 
        ? 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)'
        : 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
      borderRadius: '6px',
      animation: 'shimmer 2s infinite linear',
      animationDelay: '1s'
    }} />
    
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: isMobile ? '8px' : '16px',
      marginTop: 'auto'
    }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{
            width: '70%',
            height: '12px',
            background: themeMode === 'light' 
              ? 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)'
              : 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
            borderRadius: '2px',
            animation: 'shimmer 2s infinite linear',
            animationDelay: `${1.5 + i * 0.3}s`
          }} />
          <div style={{
            width: '50%',
            height: '16px',
            background: themeMode === 'light' 
              ? 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)'
              : 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)',
            borderRadius: '2px',
            animation: 'shimmer 2s infinite linear',
            animationDelay: `${2 + i * 0.3}s`
          }} />
        </div>
      ))}
    </div>
  </div>
);

// Enhanced error boundary component will be defined inside the main component

// CSS animations
const cssAnimations = `
  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  }
  
  @keyframes slideInUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
    40%, 43% { transform: translate3d(0,-10px,0); }
    70% { transform: translate3d(0,-5px,0); }
    90% { transform: translate3d(0,-2px,0); }
  }
`;

// Main Dashboard Component
export const DashboardSummary = () => {
  const { formatAmount, getCurrencySymbol } = useCurrency();
  const currencySymbol = getCurrencySymbol();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [dataFreshnessScore, setDataFreshnessScore] = useState(100);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  
  // Responsive breakpoints
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data freshness tracking
  useEffect(() => {
    const interval = setInterval(() => {
      const minutesSinceUpdate = (new Date() - lastUpdated) / (1000 * 60);
      const freshnessScore = Math.max(0, 100 - minutesSinceUpdate * 2);
      setDataFreshnessScore(freshnessScore);
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [lastUpdated]);

  // Enhanced error handling with retry logic
  const handleError = useCallback((err) => {
    setError(err);
    setRetryCount(prev => prev + 1);
  }, []);

  const resetError = useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);

  // Mock context hooks
  const { 
    selectedMonth, 
    selectedYear, 
    currentPeriodDisplay, 
    isCurrentMonth,
    filterTransactionsByMonth,
    filterMode,
    effectiveStartDate,
    effectiveEndDate
  } = useDateFilter();
  
  const { 
    transactions = [],
    budgets = [],
    categories = [],
    loading = false
  } = useBudget();
  
  const { themeMode } = useThemeContext();
  const { accounts: dashboardAccounts } = useAccount();

  // Fetch AI insights (moved after transactions is available)
  useEffect(() => {
    const fetchInsights = async () => {
      if (transactions.length < 5) return; // Skip if too few transactions
      
      try {
        setInsightsLoading(true);
        const response = await getAllInsights();
        if (response.success) {
          setInsights(response.insights);
        }
      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setInsightsLoading(false);
      }
    };

    fetchInsights();
  }, [transactions.length]);
  
  // Calculate metrics using date-range-aware filtering
  const monthlyMetrics = useMemo(() => {
    // Strip recurring TEMPLATES — they're blueprints, not real transactions,
    // and would double-count every recurring stream.
    const realTxs = realTransactions(transactions);
    const monthTransactions = filterTransactionsByMonth(realTxs);
    
    const income = monthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        
    const expenses = monthTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    // Total Budget — sourced from categories (matches the Budgets tab).
    const isRangeMode = filterMode === 'range' && effectiveStartDate && effectiveEndDate;
    const totalMonthlyBudget = getDashboardTotalBudget({
      categories,
      isRangeMode,
      rangeStart: effectiveStartDate,
      rangeEnd: effectiveEndDate,
      year: selectedYear,
      monthIndex: selectedMonth,
    });
    
    const balance = income - expenses;
    const budgetUsagePercent = totalMonthlyBudget > 0 ? (expenses / totalMonthlyBudget) * 100 : 0;
    const savingsRate = income > 0 ? (balance / income) * 100 : 0;
    
    const currentDate = new Date();
    // For range mode, calculate days from the range; for month mode, use calendar days
    const daysInPeriod = isRangeMode
      ? Math.max(1, Math.ceil((effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24)))
      : new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const dayOfMonth = isRangeMode ? daysInPeriod : (isCurrentMonth ? currentDate.getDate() : daysInMonth);
    const monthProgress = isRangeMode ? 100 : (isCurrentMonth ? (dayOfMonth / daysInMonth) * 100 : 100);
    const dailyBurnRate = dayOfMonth > 0 ? expenses / dayOfMonth : 0;
    const projectedMonthlySpending = isRangeMode ? expenses : (isCurrentMonth ? (expenses / Math.max(1, dayOfMonth)) * daysInMonth : expenses);
    
    return {
      income,
      expenses,
      balance,
      totalMonthlyBudget,
      budgetUsagePercent,
      savingsRate,
      transactionCount: monthTransactions.length,
      incomeTransactionCount: monthTransactions.filter(t => t.type === 'income').length,
      expenseTransactionCount: monthTransactions.filter(t => t.type === 'expense' || !t.type).length,
      avgTransactionSize: monthTransactions.filter(t => t.type === 'expense').length > 0 ? expenses / monthTransactions.filter(t => t.type === 'expense').length : 0,
      dayOfMonth,
      daysInMonth: daysInPeriod,
      monthProgress,
      dailyBurnRate,
      projectedMonthlySpending,
      daysRemaining: isRangeMode ? 0 : (isCurrentMonth ? daysInMonth - currentDate.getDate() : 0)
    };
  }, [transactions, budgets, categories, selectedMonth, selectedYear, isCurrentMonth, filterTransactionsByMonth, filterMode, effectiveStartDate, effectiveEndDate]);

  // Compute category-level budget data
  const categoryBudgetData = useMemo(() => {
    const monthTransactions = filterTransactionsByMonth(realTransactions(transactions));
    // Key: 'YYYY-MM' used by monthlyBudgets object
    const currentMonthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

    // Build spending map keyed by category name (use categoryName set by enhanceTransactionsWithCategoryInfo)
    const categorySpent = {};
    monthTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .forEach(t => {
        const catName = t.categoryName || t.category || 'Uncategorized';
        categorySpent[catName] = (categorySpent[catName] || 0) + (Number(t.amount) || 0);
      });
    
    // Map categories to budget data; prefer month-specific budget over legacy flat field
    const categoryData = categories
      .map(cat => {
        const spent = categorySpent[cat.name] || 0;
        const budget = Number(
          cat.monthlyBudgets?.[currentMonthKey] ?? cat.budget ?? 0
        );
        const utilization = budget > 0 ? (spent / budget) * 100 : 0;
        
        return {
          name: cat.name,
          icon: cat.icon || '📦',
          spent,
          budget,
          utilization,
          remaining: budget - spent
        };
      })
      .filter(c => c.budget > 0) // Only show categories with budgets
      .sort((a, b) => b.utilization - a.utilization); // Sort by utilization descending
    
    return categoryData;
  }, [transactions, categories, filterTransactionsByMonth, selectedMonth, selectedYear]);

  // Compute daily spending data for sparkline (last 14 days or current period)
  const dailySpendingData = useMemo(() => {
    const monthTransactions = filterTransactionsByMonth(realTransactions(transactions));
    const dailyTotals = {};
    
    monthTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .forEach(t => {
        const date = new Date(t.date || t.createdAt);
        const dateKey = date.toISOString().split('T')[0];
        dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + (Number(t.amount) || 0);
      });
    
    // Get last 14 days or available days
    const sortedDates = Object.keys(dailyTotals).sort();
    const last14Days = sortedDates.slice(-14);
    
    return last14Days.map(date => dailyTotals[date] || 0);
  }, [transactions, filterTransactionsByMonth]);

  // Real income sparkline data (last 14 active days in period)
  const realIncomeSparklineData = useMemo(() => {
    const monthTransactions = filterTransactionsByMonth(realTransactions(transactions));
    const dailyTotals = {};
    monthTransactions
      .filter(t => t.type === 'income')
      .forEach(t => {
        const date = new Date(t.date || t.createdAt);
        const dateKey = date.toISOString().split('T')[0];
        dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + (Number(t.amount) || 0);
      });
    const sortedDates = Object.keys(dailyTotals).sort();
    const last14Days = sortedDates.slice(-14);
    return last14Days.map(date => dailyTotals[date] || 0);
  }, [transactions, filterTransactionsByMonth]);

  // Compute previous period comparison metrics
  const previousPeriodComparison = useMemo(() => {
    // Get current period transactions
    const currentTransactions = filterTransactionsByMonth(transactions);
    const currentExpenses = currentTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const currentCount = currentTransactions.filter(t => t.type === 'expense' || !t.type).length;
    const currentIncome = currentTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    // Calculate previous period (same duration, shifted back)
    const isRangeMode = filterMode === 'range' && effectiveStartDate && effectiveEndDate;
    let previousStart, previousEnd;
    
    if (isRangeMode) {
      const rangeDuration = effectiveEndDate - effectiveStartDate;
      previousEnd = new Date(effectiveStartDate);
      previousStart = new Date(effectiveStartDate - rangeDuration);
    } else {
      // Previous month
      const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
      const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
      previousStart = new Date(prevYear, prevMonth, 1);
      previousEnd = new Date(prevYear, prevMonth + 1, 0);
    }
    
    const previousTransactions = realTransactions(transactions).filter(t => {
      const tDate = new Date(t.date || t.createdAt);
      return tDate >= previousStart && tDate <= previousEnd;
    });
    
    const previousExpenses = previousTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const previousCount = previousTransactions.filter(t => t.type === 'expense' || !t.type).length;
    const previousIncome = previousTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    // Days in the previous period (for an apples-to-apples daily-burn comparison)
    const previousDays = isRangeMode
      ? Math.max(1, Math.ceil((previousEnd - previousStart) / 86400000) + 1)
      : new Date(
          (selectedMonth === 0 ? selectedYear - 1 : selectedYear),
          (selectedMonth === 0 ? 12 : selectedMonth),
          0
        ).getDate();

    const previousDailyBurn = previousExpenses / previousDays;
    const currentDailyBurn  = currentExpenses / Math.max(1, monthlyMetrics.dayOfMonth);
    const dailyBurnRateChange = previousDailyBurn > 0
      ? ((currentDailyBurn - previousDailyBurn) / previousDailyBurn) * 100
      : 0;
    
    const avgTransactionChange = previousCount > 0
      ? ((currentExpenses / currentCount) - (previousExpenses / previousCount)) / (previousExpenses / previousCount) * 100
      : 0;

    const incomeChange = previousIncome > 0
      ? ((currentIncome - previousIncome) / previousIncome) * 100
      : 0;

    const expenseChange = previousExpenses > 0
      ? ((currentExpenses - previousExpenses) / previousExpenses) * 100
      : 0;
    
    return {
      dailyBurnRateChange: isFinite(dailyBurnRateChange) ? dailyBurnRateChange : 0,
      avgTransactionChange: isFinite(avgTransactionChange) ? avgTransactionChange : 0,
      incomeChange: isFinite(incomeChange) ? incomeChange : 0,
      expenseChange: isFinite(expenseChange) ? expenseChange : 0
    };
  }, [transactions, monthlyMetrics.dayOfMonth, filterTransactionsByMonth, filterMode, effectiveStartDate, effectiveEndDate, selectedMonth, selectedYear]);

  const styles = createStyles(themeMode, isMobile, isTablet);
  const budgetUsage = (monthlyMetrics.expenses / monthlyMetrics.totalMonthlyBudget) * 100;
  const isOverBudget = monthlyMetrics.expenses > monthlyMetrics.totalMonthlyBudget;

  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      // Simulate data refresh with actual API calls when available
      await new Promise(resolve => setTimeout(resolve, 1200));
      setLastUpdated(new Date());
      setDataFreshnessScore(100);
      resetError();
    } catch (err) {
      handleError(err);
    } finally {
      setRefreshing(false);
    }
  };

  // ErrorCard component definition
  const ErrorCard = ({ error, onRetry }) => (
    <FloatingCard
      style={{
        minHeight: isMobile ? '200px' : '256px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '12px' : '16px',
        textAlign: 'center'
      }}
      glowColor="rgba(239, 68, 68, 0.3)"
      themeMode={themeMode}
      isMobile={isMobile}
      interactive={false}
    >
      <AlertTriangle size={isMobile ? 32 : 48} color="#ef4444" />
      <div>
        <h3 style={{
          color: themeMode === 'light' ? '#1f2937' : '#ffffff',
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: '600',
          margin: '0 0 8px 0'
        }}>
          {t('dashboard.somethingWentWrong')}
        </h3>
        <p style={{
          color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
          fontSize: isMobile ? '14px' : '16px',
          margin: '0 0 16px 0',
          maxWidth: '300px'
        }}>
          {error?.message || t('dashboard.unableToLoadData')}
        </p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: isMobile ? '12px 20px' : '12px 24px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: isMobile ? '10px' : '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minHeight: '44px'
          }}
          onMouseOver={(e) => e.target.style.background = '#dc2626'}
          onMouseOut={(e) => e.target.style.background = '#ef4444'}
        >
          <RefreshCw size={16} />
          {t('dashboard.tryAgain')}
        </button>
      )}
    </FloatingCard>
  );

  // Auto-refresh when data gets stale
  useEffect(() => {
    if (dataFreshnessScore < 20 && !refreshing && retryCount < 3) {
      handleRefresh();
    }
  }, [dataFreshnessScore, refreshing, retryCount]);

  // Generate sample sparkline data
  const generateSparklineData = (base, variance = 0.2) => {
    return Array.from({ length: 7 }, (_, i) => 
      base + (Math.random() - 0.5) * base * variance
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{cssAnimations}</style>
        
        {/* Animated background elements */}
        <div style={styles.backgroundElements}>
          <div style={styles.bgCircle1} />
          <div style={styles.bgCircle2} />
          <div style={styles.bgCircle3} />
        </div>
        
        <div style={styles.contentWrapper}>
          {/* Header skeleton */}
          <div style={styles.headerSection}>
            <SkeletonCard height={isMobile ? 80 : 100} themeMode={themeMode} isMobile={isMobile} />
          </div>
          
          {/* Main grid skeleton */}
          <div style={styles.mainGrid}>
            {[...Array(isMobile ? 3 : 4)].map((_, index) => (
              <SkeletonCard 
                key={index} 
                height={styles.cardHeight.height}
                themeMode={themeMode} 
                isMobile={isMobile} 
              />
            ))}
          </div>
          
          {/* Budget section skeleton */}
          <div style={styles.budgetGrid}>
            <SkeletonCard height={300} themeMode={themeMode} isMobile={isMobile} />
            {!isMobile && (
              <div style={styles.sidebarStats}>
                <SkeletonCard height={140} themeMode={themeMode} isMobile={isMobile} />
                <SkeletonCard height={140} themeMode={themeMode} isMobile={isMobile} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error state
  if (error) {
    return (
      <div style={styles.container}>
        <style>{cssAnimations}</style>
        
        {/* Animated background elements */}
        <div style={styles.backgroundElements}>
          <div style={styles.bgCircle1} />
          <div style={styles.bgCircle2} />
          <div style={styles.bgCircle3} />
        </div>
        
        <div style={styles.contentWrapper}>
          <div style={styles.mainGrid}>
            <ErrorCard 
              error={error}
              onRetry={handleRefresh}
            />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      <style>{cssAnimations}</style>
      
      {/* Animated background elements */}
      <div style={styles.backgroundElements}>
        <div style={styles.bgCircle1} />
        <div style={styles.bgCircle2} />
        <div style={styles.bgCircle3} />
      </div>
      
      <div style={styles.contentWrapper}>
        {/* Header */}
        <div style={styles.headerSection}>
          <FloatingCard 
            style={styles.headerCard} 
            glowColor="rgba(139, 92, 246, 0.3)"
            themeMode={themeMode}
            isMobile={isMobile}
          >
            <div style={styles.headerWrapper}>
              <div>
                <h1 style={styles.headerTitle}>
                  <Sparkles size={isMobile ? 24 : 32} color="#fbbf24" />
                  {isMobile ? t('dashboard.financeHub') : t('dashboard.financialCommandCenter')}
                </h1>
                <p style={styles.headerSubtitle}>
                  <Calendar size={isMobile ? 14 : 16} />
                  <span>{currentPeriodDisplay}</span>
                  {isCurrentMonth && (
                    <>
                      <span>•</span>
                      <span>{t('dashboard.day')} {monthlyMetrics.dayOfMonth}/{monthlyMetrics.daysInMonth}</span>
                      {!isMobile && (
                        <>
                          <span>•</span>
                          <span>{monthlyMetrics.monthProgress.toFixed(1)}% {t('dashboard.complete')}</span>
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
              
              <div style={styles.headerControls}>
                
                <button
                  onClick={handleRefresh}
                  style={styles.refreshButton}
                  disabled={refreshing}
                >
                  <RefreshCw size={isMobile ? 18 : 16} style={{ 
                    animation: refreshing ? 'spin 1s linear infinite' : 'none'
                  }} />
                </button>
                
                {isCurrentMonth && (
                  <div style={styles.liveIndicator}>
                    <div style={{
                      width: isMobile ? '6px' : '8px',
                      height: isMobile ? '6px' : '8px',
                      backgroundColor: dataFreshnessScore > 80 ? '#22c55e' : dataFreshnessScore > 50 ? '#f59e0b' : '#ef4444',
                      borderRadius: '50%',
                      animation: dataFreshnessScore > 80 ? 'pulse 1s infinite' : 'none'
                    }} />
                    <span style={styles.liveText}>
                      {dataFreshnessScore > 80 ? t('dashboard.live') : dataFreshnessScore > 50 ? t('dashboard.stale') : t('dashboard.old')}
                    </span>
                  </div>
                )}
                
                <div style={{
                  ...styles.chip, 
                  ...styles.chipOutlined,
                  background: refreshing ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                }}>
                  <Clock size={isMobile ? 12 : 14} />
                  {monthlyMetrics.transactionCount} {isMobile ? t('dashboard.txn') : t('dashboard.transactions')}
                </div>
                
                {dataFreshnessScore < 90 && !refreshing && (
                  <div style={{
                    ...styles.chip,
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderColor: 'rgba(245, 158, 11, 0.3)',
                    color: '#f59e0b'
                  }}>
                    <AlertCircle size={isMobile ? 12 : 14} />
                    {Math.round(dataFreshnessScore)}% {t('dashboard.fresh')}
                  </div>
                )}

                {/* Upgrade button — visible to free/standard users */}
                {subscription && ['free', 'standard'].includes(subscription.plan) && (
                  <button
                    onClick={() => navigate('/subscription/manage')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: isMobile ? '6px 10px' : '7px 14px',
                      background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                      border: 'none',
                      borderRadius: '20px',
                      color: '#fff',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 4px 15px rgba(16,185,129,0.35)',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <Zap size={isMobile ? 12 : 14} />
                    {subscription.plan === 'free' ? t('dashboard.upgradePro', 'Upgrade') : t('dashboard.upgradePremium', 'Go Premium')}
                  </button>
                )}
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Quick Actions Bar */}
        <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
          <QuickActions />
        </div>

        {/* Inline AI Insight Cards */}
        <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
          <InlineInsightCards />
        </div>

        {/* Account Balance Summary — only when user has accounts */}
        {dashboardAccounts && dashboardAccounts.length > 0 && (
          <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
            {/* Section header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: themeMode === 'light' ? '#64748b' : 'rgba(248,250,252,0.55)',
              }}>
                {t('accounts.myAccounts', 'My Accounts')}
              </span>
              <button
                onClick={() => navigate('/accounts')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: 600,
                  color: '#10b981',
                  padding: '2px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {t('common.viewAll', 'View all')} →
              </button>
            </div>

            {/* Account chips */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? '8px' : '10px',
            }}>
              {dashboardAccounts.map((acc) => {
                const bal = acc.currentBalance ?? acc.initialBalance ?? 0;
                const isNeg = bal < 0;
                return (
                  <button
                    key={acc._id}
                    onClick={() => navigate('/accounts')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: isMobile ? '7px 12px' : '8px 14px',
                      borderRadius: '12px',
                      border: `1px solid ${acc.color ? acc.color + '44' : 'rgba(16,185,129,0.3)'}`,
                      background: themeMode === 'light'
                        ? `${acc.color ? acc.color + '18' : 'rgba(16,185,129,0.08)'}`
                        : `${acc.color ? acc.color + '22' : 'rgba(16,185,129,0.12)'}`,
                      cursor: 'pointer',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <span style={{ fontSize: isMobile ? '14px' : '16px', lineHeight: 1 }}>
                      {acc.icon || '🏦'}
                    </span>
                    <span style={{
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: 600,
                      color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
                      whiteSpace: 'nowrap',
                    }}>
                      {acc.name}
                    </span>
                    <span style={{
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: 700,
                      color: isNeg ? '#ef4444' : '#10b981',
                      whiteSpace: 'nowrap',
                    }}>
                      {isNeg ? '' : '+'}{bal.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Month Race Track + Days of Runway — answer "Am I OK this month?" */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '16px',
          marginBottom: isMobile ? '12px' : '20px',
        }}>
          <MonthRaceTrack />
          <DaysOfRunway />
          <PaycheckProgressCard />
          <DashboardLedgers />
        </div>

        {/* Main Grid */}
        {(() => {
          const balColor = monthlyMetrics.balance >= 0 ? '#10b981' : '#ef4444';
          const maxBar = Math.max(monthlyMetrics.income, monthlyMetrics.expenses) || 1;
          const incBarPct = Math.min(100, (monthlyMetrics.income / maxBar) * 100);
          const expBarPct = Math.min(100, (monthlyMetrics.expenses / maxBar) * 100);
          const muted = themeMode === 'light' ? '#64748b' : 'rgba(248,250,252,0.55)';
          const textMain = themeMode === 'light' ? '#0f172a' : '#f8fafc';
          const divider = themeMode === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)';
          const lbl = { fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: muted, marginBottom: 2 };
          const val = { fontSize: isMobile ? 13 : 15, fontWeight: 700, color: textMain };
          return (
            <div style={styles.mainGrid}>

              {/* ── Income ── */}
              <FloatingCard style={styles.cardHeight} glowColor="rgba(16,185,129,0.35)" delay={100} themeMode={themeMode} isMobile={isMobile}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 10 : 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: muted }}>{t('dashboard.income')}</span>
                    </div>
                    {(() => { const c = previousPeriodComparison.incomeChange; return (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: c >= 0 ? '#10b981' : '#ef4444', background: c >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                        {c >= 0 ? '↑' : '↓'} {Math.abs(c).toFixed(1)}%
                      </span>
                    ); })()}
                  </div>
                  <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 800, letterSpacing: '-0.03em', color: '#10b981', lineHeight: 1, fontVariantNumeric: 'tabular-nums', marginBottom: 3 }}>
                    <AnimatedCounter value={monthlyMetrics.income} prefix={currencySymbol} isMobile={isMobile} />
                  </div>
                  <div style={{ fontSize: 10, color: muted, marginBottom: isMobile ? 8 : 12, fontWeight: 500 }}>
                    {monthlyMetrics.incomeTransactionCount} {t('dashboard.sources')}
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', overflow: 'hidden', marginBottom: isMobile ? 8 : 12 }}>
                    {realIncomeSparklineData && realIncomeSparklineData.length > 1
                      ? <Sparkline data={realIncomeSparklineData} color="#10b981" width={isMobile ? 60 : 220} height={isMobile ? 26 : 36} isMobile={isMobile} />
                      : <span style={{ fontSize: 10, color: muted, display: 'flex', alignItems: 'center', gap: 4 }}><TrendingUp size={12} /> track income over time</span>
                    }
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: isMobile ? 8 : 10, borderTop: `1px solid ${divider}` }}>
                    <div><div style={lbl}>{t('dashboard.perSource', 'avg / source')}</div><div style={val}>{formatAmount(monthlyMetrics.incomeTransactionCount > 0 ? monthlyMetrics.income / monthlyMetrics.incomeTransactionCount : 0, false, isMobile, false)}</div></div>
                    <div style={{ textAlign: 'right' }}><div style={lbl}>{t('dashboard.thisMonth', 'this period')}</div><div style={{ ...val, color: '#10b981' }}>{formatAmount(monthlyMetrics.income, false, isMobile, false)}</div></div>
                  </div>
                </div>
              </FloatingCard>

              {/* ── Expenses ── */}
              <FloatingCard style={styles.cardHeight} glowColor="rgba(239,68,68,0.35)" delay={200} themeMode={themeMode} isMobile={isMobile}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 10 : 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: muted }}>{t('dashboard.expenses')}</span>
                    </div>
                    {(() => { const c = previousPeriodComparison.expenseChange; const up = c >= 0; return (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: up ? '#ef4444' : '#10b981', background: up ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                        {up ? '↑' : '↓'} {Math.abs(c).toFixed(1)}%
                      </span>
                    ); })()}
                  </div>
                  <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 800, letterSpacing: '-0.03em', color: '#ef4444', lineHeight: 1, fontVariantNumeric: 'tabular-nums', marginBottom: 3 }}>
                    <AnimatedCounter value={monthlyMetrics.expenses} prefix={currencySymbol} isMobile={isMobile} />
                  </div>
                  <div style={{ fontSize: 10, color: muted, marginBottom: isMobile ? 8 : 12, fontWeight: 500 }}>
                    {monthlyMetrics.expenseTransactionCount} {t('dashboard.transactions')}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', marginBottom: isMobile ? 8 : 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <span style={{ ...lbl, marginBottom: 0 }}>Budget used</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: budgetUsage > 90 ? '#ef4444' : budgetUsage > 70 ? '#f59e0b' : '#10b981' }}>{budgetUsage.toFixed(0)}%</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 99, background: themeMode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(100, budgetUsage)}%`, borderRadius: 99, background: budgetUsage > 90 ? '#ef4444' : budgetUsage > 70 ? '#f59e0b' : '#10b981', transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: isMobile ? 8 : 10, borderTop: `1px solid ${divider}` }}>
                    <div><div style={lbl}>{t('dashboard.dailyAvg', 'daily avg')}</div><div style={val}>{formatAmount(monthlyMetrics.dailyBurnRate, false, isMobile, false)}</div></div>
                    <div style={{ textAlign: 'right' }}><div style={lbl}>Budget left</div><div style={{ ...val, color: isOverBudget ? '#ef4444' : '#10b981' }}>{formatAmount(Math.max(0, monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses), false, isMobile, false)}</div></div>
                  </div>
                </div>
              </FloatingCard>

              {/* ── Net Balance ── */}
              <FloatingCard style={styles.cardHeight} glowColor={monthlyMetrics.balance >= 0 ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'} delay={300} themeMode={themeMode} isMobile={isMobile}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 10 : 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: balColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: muted }}>{t('dashboard.netBalance')}</span>
                    </div>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: monthlyMetrics.savingsRate >= 0 ? '#10b981' : '#ef4444', background: monthlyMetrics.savingsRate >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                      {monthlyMetrics.savingsRate.toFixed(1)}% saved
                    </span>
                  </div>
                  <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 800, letterSpacing: '-0.03em', color: balColor, lineHeight: 1, fontVariantNumeric: 'tabular-nums', marginBottom: 3 }}>
                    <AnimatedCounter value={Math.abs(monthlyMetrics.balance)} prefix={monthlyMetrics.balance >= 0 ? `${currencySymbol}+` : `${currencySymbol}−`} isMobile={isMobile} />
                  </div>
                  <div style={{ fontSize: 10, color: muted, marginBottom: isMobile ? 8 : 10, fontWeight: 500 }}>
                    {monthlyMetrics.balance >= 0 ? t('dashboard.positive', 'surplus') : t('dashboard.deficit', 'deficit')} this period
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 6, marginBottom: isMobile ? 8 : 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#10b981', width: 22, flexShrink: 0 }}>INC</span>
                      <div style={{ flex: 1, height: 5, borderRadius: 99, background: themeMode === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.09)' }}>
                        <div style={{ height: '100%', width: `${incBarPct}%`, background: '#10b981', borderRadius: 99 }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#ef4444', width: 22, flexShrink: 0 }}>EXP</span>
                      <div style={{ flex: 1, height: 5, borderRadius: 99, background: themeMode === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.09)' }}>
                        <div style={{ height: '100%', width: `${expBarPct}%`, background: '#ef4444', borderRadius: 99 }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: isMobile ? 8 : 10, borderTop: `1px solid ${divider}` }}>
                    <div><div style={lbl}>{t('dashboard.income')}</div><div style={{ ...val, color: '#10b981' }}>{formatAmount(monthlyMetrics.income, false, isMobile, false)}</div></div>
                    <div style={{ textAlign: 'right' }}><div style={lbl}>{t('dashboard.expenses')}</div><div style={{ ...val, color: '#ef4444' }}>{formatAmount(monthlyMetrics.expenses, false, isMobile, false)}</div></div>
                  </div>
                </div>
              </FloatingCard>

            </div>
          );
        })()}

        {/* Budget Progress Section */}
        <div style={styles.budgetGrid}>
          
          {/* Budget Overview */}
          <FloatingCard 
            style={styles.budgetCard} 
            glowColor="rgba(59, 130, 246, 0.4)"
            delay={400}
            themeMode={themeMode}
            isMobile={isMobile}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={styles.budgetHeader}>
                <div style={styles.cardHeaderLeft}>
                  <div style={{...styles.iconContainer, ...styles.iconContainerBlue}}>
                    <Target size={isMobile ? 20 : 24} color="#3b82f6" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={styles.budgetTitle}>{isMobile ? t('dashboard.budgetControl') : t('dashboard.budgetControlCenter')}</h3>
                    <p style={styles.budgetSubtitle}>{t('dashboard.smartInsights')}</p>
                  </div>
                </div>
                
                <div style={{
                  ...styles.statusBadge,
                  ...(isOverBudget 
                    ? styles.statusBadgeRed 
                    : budgetUsage > 85 
                      ? styles.statusBadgeYellow
                      : styles.statusBadgeGreen)
                }}>
                  {isOverBudget ? <AlertTriangle size={isMobile ? 14 : 16} /> : <ShieldCheck size={isMobile ? 14 : 16} />}
                  <span>
                    {isOverBudget ? t('dashboard.overBudget') : budgetUsage > 85 ? (isMobile ? t('dashboard.nearLimit') : t('dashboard.almostAtLimit')) : t('dashboard.onTrack')}
                  </span>
                </div>
              </div>
              
              <div style={{ flex: 1 }}>
                {/* Visual Stat Cards */}
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                  gap: isMobile ? '8px' : '12px',
                  marginBottom: isMobile ? '16px' : '20px'
                }}>
                  {/* Total Budget Card */}
                  <div style={{
                    ...styles.budgetVisualStatCard,
                    ...styles.budgetStatCardBlue
                  }}>
                    <div style={styles.budgetStatCardIcon}>
                      <Target size={isMobile ? 18 : 20} color="#3b82f6" />
                    </div>
                    <div style={styles.budgetStatCardContent}>
                      <div style={styles.budgetStatCardLabel}>{t('dashboard.totalBudget')}</div>
                      <div style={{...styles.budgetStatCardValue, color: '#3b82f6'}}>
                        {formatAmount(monthlyMetrics.totalMonthlyBudget, false, isMobile, false)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Spent Card */}
                  <div style={{
                    ...styles.budgetVisualStatCard,
                    ...(budgetUsage > 90 ? styles.budgetStatCardRed : budgetUsage > 70 ? styles.budgetStatCardAmber : styles.budgetStatCardGreen)
                  }}>
                    <div style={styles.budgetStatCardIcon}>
                      <Flame size={isMobile ? 18 : 20} color={budgetUsage > 90 ? '#ef4444' : budgetUsage > 70 ? '#f59e0b' : '#22c55e'} />
                    </div>
                    <div style={styles.budgetStatCardContent}>
                      <div style={styles.budgetStatCardLabel}>{t('dashboard.spent')}</div>
                      <div style={{
                        ...styles.budgetStatCardValue, 
                        color: budgetUsage > 90 ? '#ef4444' : budgetUsage > 70 ? '#f59e0b' : '#22c55e'
                      }}>
                        {formatAmount(monthlyMetrics.expenses, false, isMobile, false)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Remaining Card */}
                  <div style={{
                    ...styles.budgetVisualStatCard,
                    ...(monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses >= 0 ? styles.budgetStatCardGreen : styles.budgetStatCardRed)
                  }}>
                    <div style={styles.budgetStatCardIcon}>
                      <Wallet size={isMobile ? 18 : 20} color={monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses >= 0 ? '#22c55e' : '#ef4444'} />
                    </div>
                    <div style={styles.budgetStatCardContent}>
                      <div style={styles.budgetStatCardLabel}>{t('dashboard.remaining')}</div>
                      <div style={{
                        ...styles.budgetStatCardValue,
                        color: monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses >= 0 ? '#22c55e' : '#ef4444'
                      }}>
                        {formatAmount(Math.abs(monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses), false, isMobile, false)}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Dual-Track Progress Bar */}
                <div style={styles.enhancedProgressBar}>
                  <div style={styles.progressTrack}>
                    <div 
                      style={{
                        ...styles.progressFillGradient,
                        width: `${Math.min(budgetUsage, 100)}%`,
                        background: isOverBudget 
                          ? '#ef4444' 
                          : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)'
                      }}
                    />
                    
                    {/* Tick marks at 25%, 50%, 75% */}
                    <div style={styles.progressTickMarks}>
                      <div style={styles.progressTick} />
                      <div style={styles.progressTick} />
                    </div>
                  </div>
                  
                  {/* Time Progress Marker */}
                  <div 
                    style={{
                      ...styles.timeProgressMarker,
                      left: `${monthlyMetrics.monthProgress}%`
                    }}
                  />
                  
                  {/* Pace Indicator Dot */}
                  {monthlyMetrics.monthProgress > 0 && (
                    <div 
                      style={{
                        ...styles.paceDot,
                        left: `${budgetUsage}%`,
                        background: (budgetUsage / monthlyMetrics.monthProgress) > 1.1 ? '#ef4444' : '#22c55e'
                      }}
                    />
                  )}
                  
                  <div style={styles.progressLabelsEnhanced}>
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '8px',
                    gap: '16px',
                    fontSize: isMobile ? '11px' : '12px',
                    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)' }} />
                      <span>{budgetUsage.toFixed(1)}% {t('dashboard.spent')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '12px', height: '2px', background: themeMode === 'light' ? '#1f2937' : '#ffffff' }} />
                      <span>{monthlyMetrics.monthProgress.toFixed(0)}% {t('dashboard.timeElapsed')}</span>
                    </div>
                  </div>
                </div>
                
                {/* Safe to Spend Callout */}
                {monthlyMetrics.daysRemaining > 0 && (
                  <div style={{
                    ...styles.safeToSpendCard,
                    ...((() => {
                      const remainingBudget = monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses;
                      const safeToSpend = remainingBudget / monthlyMetrics.daysRemaining;
                      if (safeToSpend <= 0) return styles.safeToSpendRed;
                      if (safeToSpend < monthlyMetrics.dailyBurnRate * 0.8) return styles.safeToSpendAmber;
                      return styles.safeToSpendGreen;
                    })())
                  }}>
                    <div style={styles.safeToSpendIcon}>
                      <PiggyBank size={isMobile ? 20 : 24} color={(() => {
                        const remainingBudget = monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses;
                        const safeToSpend = remainingBudget / monthlyMetrics.daysRemaining;
                        if (safeToSpend <= 0) return '#ef4444';
                        if (safeToSpend < monthlyMetrics.dailyBurnRate * 0.8) return '#f59e0b';
                        return '#22c55e';
                      })()} />
                    </div>
                    <div style={styles.safeToSpendContent}>
                      <div style={styles.safeToSpendAmount}>
                        {formatAmount((monthlyMetrics.totalMonthlyBudget - monthlyMetrics.expenses) / monthlyMetrics.daysRemaining, false, isMobile, false)}<span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '500' }}> / day</span>
                      </div>
                      <div style={styles.safeToSpendLabel}>
                        Safe to spend · {monthlyMetrics.daysRemaining} {t('dashboard.daysLeft')}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Per-Category Budget Breakdown */}
                {categoryBudgetData.length > 0 && (
                  <div style={styles.categoryBudgetSection}>
                    <div style={styles.categoryBudgetHeader}>
                      <h4 style={styles.categoryBudgetTitle}>Category Budget Usage</h4>
                      <span style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)'
                      }}>
                        Top {showAllCategories ? categoryBudgetData.length : Math.min(3, categoryBudgetData.length)}
                      </span>
                    </div>
                    
                    {categoryBudgetData.slice(0, showAllCategories ? undefined : 3).map((cat, idx) => (
                      <div key={cat.name} style={styles.categoryBudgetRow}>
                        <div style={styles.categoryBudgetIcon}>{cat.icon}</div>
                        <div style={styles.categoryBudgetInfo}>
                          <div style={styles.categoryBudgetName}>{cat.name}</div>
                          <div style={styles.categoryBudgetProgressBar}>
                            <div 
                              style={{
                                ...styles.categoryBudgetProgressFill,
                                width: `${Math.min(cat.utilization, 100)}%`,
                                background: cat.utilization > 90 ? '#ef4444' : cat.utilization > 70 ? '#f59e0b' : '#22c55e'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{
                          ...styles.categoryBudgetAmount,
                          color: cat.utilization > 90 ? '#ef4444' : cat.utilization > 70 ? '#f59e0b' : '#22c55e'
                        }}>
                          {formatAmount(cat.spent, false, isMobile, false)} / {formatAmount(cat.budget, false, isMobile, false)}
                        </div>
                      </div>
                    ))}
                    
                    {categoryBudgetData.length > 3 && (
                      <button 
                        onClick={() => setShowAllCategories(!showAllCategories)}
                        style={styles.showMoreButton}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = themeMode === 'light' ? 'rgba(31, 41, 55, 0.4)' : 'rgba(255, 255, 255, 0.4)';
                          e.target.style.background = themeMode === 'light' ? 'rgba(31, 41, 55, 0.05)' : 'rgba(255, 255, 255, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = themeMode === 'light' ? 'rgba(31, 41, 55, 0.2)' : 'rgba(255, 255, 255, 0.2)';
                          e.target.style.background = 'transparent';
                        }}
                      >
                        {showAllCategories ? (
                          <>
                            <Minus size={14} />
                            Show Less
                          </>
                        ) : (
                          <>
                            <Plus size={14} />
                            Show {categoryBudgetData.length - 3} More
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              <div style={styles.insightCard}>
                <div style={styles.insightHeader}>
                  <Brain size={isMobile ? 14 : 16} color="#3b82f6" />
                  <span style={styles.insightTitle}>{t('dashboard.aiFinancialInsight')}</span>
                  {dataFreshnessScore > 80 && (
                    <div style={{
                      width: '6px',
                      height: '6px',
                      backgroundColor: '#22c55e',
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }} />
                  )}
                </div>
                <p style={styles.insightText}>
                  {isOverBudget 
                    ? t('dashboard.budgetAdvice.overBudget', { amount: formatAmount(monthlyMetrics.expenses - monthlyMetrics.totalMonthlyBudget, false, isMobile, false) })
                    : budgetUsage > 90
                      ? t('dashboard.budgetAdvice.nearLimit', { budgetUsage: budgetUsage.toFixed(0), daysRemaining: monthlyMetrics.daysRemaining })
                      : budgetUsage > 70
                        ? t('dashboard.budgetAdvice.goodProgress', { budgetUsage: budgetUsage.toFixed(0), monthProgress: monthlyMetrics.monthProgress.toFixed(0) })
                        : t('dashboard.budgetAdvice.excellentControl', { budgetUsage: budgetUsage.toFixed(0) })
                  }
                </p>
                {monthlyMetrics.savingsRate > 20 && (
                  <div style={{
                    marginTop: isMobile ? '8px' : '12px',
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: isMobile ? '6px' : '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px'
                  }}>
                    <Trophy size={isMobile ? 12 : 14} color="#22c55e" />
                    <span style={{
                      ...styles.insightText,
                      color: '#22c55e',
                      fontWeight: '600',
                      fontSize: isMobile ? '11px' : '13px'
                    }}>
                      {t('dashboard.outstandingSavings', { savingsRate: monthlyMetrics.savingsRate.toFixed(1) })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </FloatingCard>

          {/* Quick Stats */}
          <div style={styles.sidebarStats}>
            
            {/* Circular Progress */}
            <FloatingCard 
              style={styles.sidebarStatCard}
              glowColor="rgba(168, 85, 247, 0.4)" 
              delay={500} 
              themeMode={themeMode}
              isMobile={isMobile}
            >
              <div style={{ textAlign: 'center' }}>
                <h4 style={{
                  ...styles.cardTitle,
                  marginBottom: isMobile ? '12px' : '16px'
                }}>{t('dashboard.monthProgress')}</h4>
                <div style={styles.progressRingContainer}>
                  <ProgressRing 
                    progress={monthlyMetrics.monthProgress} 
                    color="#A855F7"
                    size={isMobile ? 70 : 100}
                    backgroundColor={themeMode === 'light' ? 'rgba(31, 41, 55, 0.1)' : 'rgba(255, 255, 255, 0.1)'}
                    isMobile={isMobile}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        color: themeMode === 'light' ? '#1f2937' : 'white', 
                        fontWeight: '700', 
                        fontSize: isMobile ? '14px' : '18px' 
                      }}>
                        {monthlyMetrics.monthProgress.toFixed(0)}%
                      </div>
                      <div style={{ 
                        color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)', 
                        fontSize: isMobile ? '10px' : '12px' 
                      }}>
                        {t('dashboard.completeCapital')}
                      </div>
                    </div>
                  </ProgressRing>
                </div>
                <div style={styles.progressRingStats}>
                  <div>
                    <div style={styles.statLabel}>{t('dashboard.daysLeft')}</div>
                    <div style={styles.statValue}>{monthlyMetrics.daysRemaining || t('dashboard.completeCapital')}</div>
                  </div>
                  <div>
                    <div style={styles.statLabel}>{t('dashboard.dailyBurn')}</div>
                    <div style={styles.statValue}>{formatAmount(monthlyMetrics.dailyBurnRate, false, isMobile, false)}</div>
                  </div>
                </div>
              </div>
            </FloatingCard>

            {/* Enhanced Speed Metrics with Actions */}
            <FloatingCard 
              style={styles.sidebarStatCard}
              glowColor="rgba(245, 158, 11, 0.4)" 
              delay={600} 
              themeMode={themeMode}
              isMobile={isMobile}
              onClick={() => {/* Add analytics navigation */}}
            >
              <div style={styles.cardHeaderLeft}>
                <div style={{
                  padding: isMobile ? '6px' : '8px',
                  background: 'rgba(245, 158, 11, 0.2)',
                  borderRadius: isMobile ? '6px' : '8px',
                  border: '1px solid rgba(245, 158, 11, 0.3)'
                }}>
                  <Gauge size={isMobile ? 16 : 20} color="#f59e0b" />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    color: themeMode === 'light' ? '#1f2937' : 'white', 
                    fontWeight: '600',
                    fontSize: isMobile ? '14px' : '16px',
                    margin: 0
                  }}>
                    {t('dashboard.speedMetrics')}
                  </h4>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '2px'
                  }}>
                    <CircleDot size={8} color={
                      monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) * 1.2 
                        ? '#ef4444' 
                        : monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) 
                          ? '#f59e0b' 
                          : '#22c55e'
                    } />
                    <span style={{
                      fontSize: isMobile ? '10px' : '12px',
                      color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.6)'
                    }}>
                      {monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) * 1.2 ? t('dashboard.highSpending') : 
                       monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) ? t('dashboard.moderate') : t('dashboard.controlled')}
                    </span>
                  </div>
                </div>
                <ChevronRight size={isMobile ? 14 : 16} color={themeMode === 'light' ? 'rgba(31, 41, 55, 0.4)' : 'rgba(255, 255, 255, 0.4)'} />
              </div>
              
              {/* Daily Spending Sparkline */}
              {dailySpendingData.length > 0 && (
                <div style={styles.speedMetricsSparkline}>
                  <div style={styles.speedMetricsSparklineHeader}>
                    <span style={styles.speedMetricsSparklineTitle}>Daily Spending Trend</span>
                    <span style={{
                      fontSize: isMobile ? '10px' : '11px',
                      color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                    }}>
                      Last {dailySpendingData.length} days
                    </span>
                  </div>
                  <Sparkline 
                    data={dailySpendingData} 
                    color={monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) * 1.2 ? '#ef4444' : '#f59e0b'}
                    width={isMobile ? 150 : 200}
                    height={isMobile ? 40 : 50}
                    isMobile={isMobile}
                  />
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: isMobile ? '12px' : '16px', 
                marginTop: isMobile ? '12px' : '16px' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ 
                    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>{t('dashboard.dailyBurnRate')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {previousPeriodComparison.dailyBurnRateChange !== 0 && (
                      <span style={{
                        ...styles.speedMetricsComparison,
                        ...(previousPeriodComparison.dailyBurnRateChange > 0 ? styles.comparisonUp : styles.comparisonDown)
                      }}>
                        {previousPeriodComparison.dailyBurnRateChange > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(previousPeriodComparison.dailyBurnRateChange).toFixed(1)}%
                      </span>
                    )}
                    <span style={{ 
                      color: themeMode === 'light' ? '#1f2937' : 'white', 
                      fontWeight: '600',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {formatAmount(monthlyMetrics.dailyBurnRate, false, isMobile, false)}
                    </span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ 
                    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>{t('dashboard.avgTransaction')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {previousPeriodComparison.avgTransactionChange !== 0 && (
                      <span style={{
                        ...styles.speedMetricsComparison,
                        ...(previousPeriodComparison.avgTransactionChange > 0 ? styles.comparisonUp : styles.comparisonDown)
                      }}>
                        {previousPeriodComparison.avgTransactionChange > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(previousPeriodComparison.avgTransactionChange).toFixed(1)}%
                      </span>
                    )}
                    <span style={{ 
                      color: themeMode === 'light' ? '#1f2937' : 'white', 
                      fontWeight: '600',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {formatAmount(monthlyMetrics.avgTransactionSize, false, isMobile, false)}
                    </span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ 
                    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>{t('dashboard.frequency')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ 
                      color: themeMode === 'light' ? '#1f2937' : 'white', 
                      fontWeight: '600',
                      fontSize: isMobile ? '12px' : '14px'
                    }}>
                      {monthlyMetrics.dayOfMonth > 0 ? (monthlyMetrics.expenseTransactionCount / monthlyMetrics.dayOfMonth).toFixed(1) : 0}{t('dashboard.perDay')}
                    </span>
                    {monthlyMetrics.expenseTransactionCount / monthlyMetrics.dayOfMonth > 3 && (
                      <AlertCircle size={12} color="#f59e0b" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Quick action suggestions */}
              <div style={{
                marginTop: isMobile ? '12px' : '16px',
                paddingTop: isMobile ? '12px' : '16px',
                borderTop: `1px solid ${themeMode === 'light' ? 'rgba(31, 41, 55, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
                display: 'flex',
                gap: isMobile ? '6px' : '8px',
                flexWrap: 'wrap'
              }}>
                {monthlyMetrics.dailyBurnRate > (monthlyMetrics.totalMonthlyBudget / 30) && (
                  <div style={{
                    ...styles.chip,
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    fontSize: isMobile ? '10px' : '11px',
                    cursor: 'pointer'
                  }}>
                    <AlertTriangle size={10} />
                    {t('dashboard.reduceSpending')}
                  </div>
                )}
                {monthlyMetrics.savingsRate > 15 && (
                  <div style={{
                    ...styles.chip,
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderColor: 'rgba(34, 197, 94, 0.3)',
                    color: '#22c55e',
                    fontSize: isMobile ? '10px' : '11px',
                    cursor: 'pointer'
                  }}>
                    <PiggyBank size={10} />
                    {t('dashboard.investSurplus')}
                  </div>
                )}
              </div>
            </FloatingCard>

          </div>
        </div>

        {/* AI Insights Section */}
        <div style={{ marginTop: isMobile ? '16px' : '24px' }}>
          <InsightsSummaryCard insights={insights} loading={insightsLoading} />
        </div>

      </div>
    </div>
  );
};

export default DashboardSummary;